import { Controller } from "@hotwired/stimulus"

// Public Help Center search — server-side variant (HC-6, #265).
//
// Behaviourally identical to help_search_controller (Fuse.js client-side)
// but defers the actual scoring to a Postgres FTS endpoint. Each keystroke
// fetches `?q=<query>` JSON from the search endpoint; results render with
// the same DOM shape as the Fuse path so the CSS is shared.
//
// Why a second controller instead of toggling inside the existing one:
// gates and behaviour diverge enough (no client index to lazy-load; abort
// in-flight requests on new keystrokes; defer-debounce semantics) that
// branching the existing class would obscure both paths. The feature flag
// at the view layer picks one or the other.
export default class extends Controller {
  static targets = ["input", "results"]
  static values = {
    endpoint: String,
    debounce: { type: Number, default: 200 },
    minChars: { type: Number, default: 2 }
  }

  connect() {
    this.debounceTimer = null
    this.abortController = null
  }

  disconnect() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = null
    }
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }
  }

  search(event) {
    const query = (event.target.value || "").trim()
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => this._runSearch(query), this.debounceValue)
  }

  async _runSearch(query) {
    if (query.length < this.minCharsValue) {
      this._hideResults()
      return
    }

    // Cancel any in-flight fetch so a slow earlier response can't overwrite
    // results for a newer query. Each search owns its own AbortController.
    if (this.abortController) this.abortController.abort()
    this.abortController = new AbortController()

    let results
    try {
      const url = `${this.endpointValue}.json?q=${encodeURIComponent(query)}`
      const res = await fetch(url, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
        signal: this.abortController.signal
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      results = await res.json()
    } catch (err) {
      if (err.name === "AbortError") return
      console.error("[help-search-server] fetch failed", err)
      this._renderError()
      return
    }

    this._renderResults(results, query)
  }

  _renderResults(items, query) {
    if (!this.hasResultsTarget) return
    if (items.length === 0) {
      this.resultsTarget.innerHTML =
        `<p class="help-center__search-empty">${this._safeText(this._t("no_results"))}</p>`
      this.resultsTarget.hidden = false
      return
    }

    const html = items.map((item) => this._renderItem(item, query)).join("")
    this.resultsTarget.innerHTML = `<ul class="help-center__search-list">${html}</ul>`
    this.resultsTarget.hidden = false
  }

  _renderItem(item, query) {
    const href = `/help-center/${encodeURIComponent(item.category_slug)}/${encodeURIComponent(item.slug)}`
    const tags = (item.tags || []).slice(0, 3)
      .map((t) => `<span class="help-center__tag-chip">${this._safeText(t)}</span>`)
      .join("")
    return `
      <li class="help-center__search-item">
        <a href="${href}" class="help-center__search-link">
          <span class="help-center__search-title">${this._safeText(item.title)}</span>
          <span class="help-center__search-excerpt">${this._safeText(item.body_excerpt || "")}</span>
          ${tags ? `<span class="help-center__search-tags">${tags}</span>` : ""}
        </a>
      </li>
    `
  }

  _renderError() {
    if (!this.hasResultsTarget) return
    this.resultsTarget.innerHTML =
      `<p class="help-center__search-empty">${this._safeText(this._t("error"))}</p>`
    this.resultsTarget.hidden = false
  }

  _hideResults() {
    if (!this.hasResultsTarget) return
    this.resultsTarget.innerHTML = ""
    this.resultsTarget.hidden = true
  }

  // Tiny escape so admin-authored Help Topic content rendered via innerHTML
  // stays inert. Mirrors help_search_controller for consistency.
  _safeText(value) {
    return String(value || "").replace(/[&<>"']/g, (c) => ({
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      "\"": "&quot;",
      "'": "&#39;"
    })[c])
  }

  _t(key) {
    const map = {
      no_results: document.documentElement.lang === "en" ? "No results found." : "Nenhum resultado encontrado.",
      error: document.documentElement.lang === "en" ? "Could not load search." : "Não foi possível carregar a busca."
    }
    return map[key]
  }
}
